import { useState, useEffect } from "react";
import { ContainerCard } from "../../components/ui/cards";
import { Input, Select, TimePicker, InputNumber, Switch, Spin } from "antd";
import { Settings, Save } from "lucide-react";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { AI_CALLER_CONFIG_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import dayjs from "dayjs";

const { TextArea } = Input;

// ── constants ─────────────────────────────────────────────────────────────────

// Values must match backend AICallerConfig.LANGUAGE_CHOICES codes.
const LANGUAGE_OPTIONS = [
    { value: "en", label: "English" },
    { value: "hi", label: "Hindi" },
    { value: "ta", label: "Tamil" },
    { value: "ml", label: "Malayalam" },
    { value: "te", label: "Telugu" },
];


const INBOUND_TARGET_OPTIONS = [
    { value: "engage", label: "Engage Enquiry" },
    { value: "trustedge", label: "Gro Wing CRM Customer" },
];

const EMPTY_CONFIG = {
    company_name: "",
    agent_name: "",
    twilio_phone_number: "",
    elevenlabs_voice_id: "",
    default_language: "en",
    calling_hours_start: null,
    calling_hours_end: null,
    max_retries: 3,
    retry_delay_minutes: 30,
    max_call_duration_seconds: 300,
    knowledge_base: "",
    system_prompt: "",
    is_enabled: true,
    inbound_enabled: false,
    inbound_lead_target: "engage",
    inbound_greeting_template: "",
};

// ── main component ────────────────────────────────────────────────────────────

export default function AICallerSettings() {
    const { fetch, update } = useApiMethods();

    const [config, setConfig] = useState({ ...EMPTY_CONFIG });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const loadConfig = async () => {
        setLoading(true);
        const res = await fetch(AI_CALLER_CONFIG_URL);
        if (res?.status === "success" && res.data) {
            const d = res.data;
            setConfig({
                company_name: d.company_name || "",
                agent_name: d.agent_name || "",
                twilio_phone_number: d.twilio_phone_number || "",
                elevenlabs_voice_id: d.elevenlabs_voice_id || "",
                default_language: d.default_language || "en",
                calling_hours_start: d.calling_hours_start ? dayjs(d.calling_hours_start, "HH:mm") : null,
                calling_hours_end: d.calling_hours_end ? dayjs(d.calling_hours_end, "HH:mm") : null,
                max_retries: d.max_retries ?? 3,
                retry_delay_minutes: d.retry_delay_minutes ?? 30,
                max_call_duration_seconds: d.max_call_duration_seconds ?? 300,
                knowledge_base: d.knowledge_base || "",
                system_prompt: d.system_prompt || "",
                is_enabled: d.is_enabled ?? true,
                inbound_enabled: d.inbound_enabled ?? false,
                inbound_lead_target: d.inbound_lead_target || "engage",
                inbound_greeting_template: d.inbound_greeting_template || "",
            });
        }
        setLoading(false);
    };

    useEffect(() => { loadConfig(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSave = async () => {
        setSaving(true);
        const payload = {
            ...config,
            calling_hours_start: config.calling_hours_start ? config.calling_hours_start.format("HH:mm") : null,
            calling_hours_end: config.calling_hours_end ? config.calling_hours_end.format("HH:mm") : null,
        };
        const res = await update(AI_CALLER_CONFIG_URL, payload);
        setSaving(false);
        if (res?.status === "success") {
            ToastMsg("success", "Settings saved");
        } else {
            ToastMsg("error", res?.message || "Failed to save settings");
        }
    };

    const set = (key, value) => setConfig((prev) => ({ ...prev, [key]: value }));

    if (loading) {
        return (
            <ContainerCard>
                <div className="flex items-center justify-center py-24">
                    <Spin size="large" />
                </div>
            </ContainerCard>
        );
    }

    return (
        <ContainerCard>
            {/* Header */}
            <div className="flex items-center justify-between mb-4 p-2">
                <div>
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                        <Settings className="h-5 w-5 text-teal-600" /> AI Caller Settings
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Configure your AI calling agent</p>
                </div>
                <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 text-sm"
                >
                    <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save"}
                </Button>
            </div>

            <div className="space-y-6 px-2 pb-4">
                {/* General settings */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">General</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Company Name</label>
                            <Input
                                value={config.company_name}
                                onChange={(e) => set("company_name", e.target.value)}
                                placeholder="Your company name"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Agent Name</label>
                            <Input
                                value={config.agent_name}
                                onChange={(e) => set("agent_name", e.target.value)}
                                placeholder="e.g. Sarah"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Twilio Phone Number</label>
                            <Input
                                value={config.twilio_phone_number}
                                onChange={(e) => set("twilio_phone_number", e.target.value)}
                                placeholder="+17126421714"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">ElevenLabs Voice ID</label>
                            <Input
                                value={config.elevenlabs_voice_id}
                                onChange={(e) => set("elevenlabs_voice_id", e.target.value)}
                                placeholder="Voice ID"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Default Language</label>
                            <Select
                                value={config.default_language}
                                onChange={(val) => set("default_language", val)}
                                options={LANGUAGE_OPTIONS}
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>

                {/* Calling hours & limits */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Calling Hours & Limits</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Calling Hours Start</label>
                            <TimePicker
                                value={config.calling_hours_start}
                                onChange={(val) => set("calling_hours_start", val)}
                                format="HH:mm"
                                className="w-full"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Calling Hours End</label>
                            <TimePicker
                                value={config.calling_hours_end}
                                onChange={(val) => set("calling_hours_end", val)}
                                format="HH:mm"
                                className="w-full"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Max Retries</label>
                            <InputNumber
                                value={config.max_retries}
                                onChange={(val) => set("max_retries", val)}
                                min={0}
                                max={10}
                                className="w-full"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Retry Delay (minutes)</label>
                            <InputNumber
                                value={config.retry_delay_minutes}
                                onChange={(val) => set("retry_delay_minutes", val)}
                                min={1}
                                max={1440}
                                className="w-full"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Max Call Duration (seconds)</label>
                            <InputNumber
                                value={config.max_call_duration_seconds}
                                onChange={(val) => set("max_call_duration_seconds", val)}
                                min={30}
                                max={3600}
                                className="w-full"
                            />
                        </div>
                        <div className="flex items-end">
                            <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Enabled</label>
                                <Switch
                                    checked={config.is_enabled}
                                    onChange={(val) => set("is_enabled", val)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Inbound calls */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Inbound Calls</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                        When on, the AI agent answers people who dial your Twilio number. Known callers
                        are picked up at their current stage; first-time callers are captured as a
                        new lead. Calling hours do not apply to inbound calls.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="flex items-end">
                            <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Answer Inbound Calls</label>
                                <Switch
                                    checked={config.inbound_enabled}
                                    onChange={(val) => set("inbound_enabled", val)}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">New Caller Becomes</label>
                            <Select
                                value={config.inbound_lead_target}
                                onChange={(val) => set("inbound_lead_target", val)}
                                options={INBOUND_TARGET_OPTIONS}
                                disabled={!config.inbound_enabled}
                                className="w-full"
                            />
                        </div>
                    </div>
                    <div className="mt-4">
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                            Inbound Greeting (optional)
                        </label>
                        <TextArea
                            value={config.inbound_greeting_template}
                            onChange={(e) => set("inbound_greeting_template", e.target.value)}
                            rows={2}
                            disabled={!config.inbound_enabled}
                            placeholder="Leave blank for a language-appropriate default. Placeholders: {customer_name}, {company_name}, {agent_name}"
                        />
                    </div>
                </div>

                {/* Knowledge base & system prompt */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">AI Configuration</h3>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Knowledge Base</label>
                            <TextArea
                                value={config.knowledge_base}
                                onChange={(e) => set("knowledge_base", e.target.value)}
                                rows={8}
                                placeholder="Enter product/service knowledge base that the AI agent will use during calls..."
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">System Prompt</label>
                            <TextArea
                                value={config.system_prompt}
                                onChange={(e) => set("system_prompt", e.target.value)}
                                rows={12}
                                placeholder="Enter the system prompt for the AI calling agent..."
                            />
                        </div>
                    </div>
                </div>
            </div>
        </ContainerCard>
    );
}
